import type { SyncTableKey } from './syncTables';

/**
 * The only file that knows `/api/sync/*`'s endpoint paths and wire shapes —
 * same role `src/app/auth/authApi.ts` plays for auth. `credentials: 'include'`
 * on every call is what attaches the session cookie; a network failure is
 * always distinguished from a definite server answer via the discriminated
 * `outcome`, never conflated (a caught `fetch` throw is not "the server said
 * no", it's "we don't know").
 */

export interface PushRowResult {
  id: string;
  /**
   * `'stale'`: the row is currently tombstoned server-side, and this
   * device's own cursor proves it hasn't observed that tombstone yet — the
   * server rejected the resurrection outright rather than applying it. No
   * local action is needed: the row was never a local tombstone (nothing to
   * purge), and this device's next pull will bring the authoritative
   * tombstone down and correct its local view on its own.
   */
  status: 'inserted' | 'updated' | 'unchanged' | 'forbidden' | 'rejected' | 'stale';
  reason?: string;
}

export type PushOutcome =
  | { outcome: 'success'; results: Partial<Record<SyncTableKey, PushRowResult[]>> }
  | { outcome: 'unauthenticated' }
  | { outcome: 'unavailable' }
  | { outcome: 'network-error' }
  | { outcome: 'server-error'; status: number };

export type PullOutcome =
  | {
      outcome: 'success';
      cursor: string;
      hasMore: boolean;
      tables: Partial<Record<SyncTableKey, { rows: Record<string, unknown>[]; hasMore: boolean }>>;
    }
  | { outcome: 'unauthenticated' }
  | { outcome: 'unavailable' }
  | { outcome: 'network-error' }
  | { outcome: 'server-error'; status: number };

export type MediaUploadOutcome =
  | { outcome: 'success' }
  | { outcome: 'not-found' }
  | { outcome: 'unauthenticated' }
  | { outcome: 'network-error' }
  | { outcome: 'server-error'; status: number };

export type MediaDownloadOutcome =
  | { outcome: 'success'; blob: Blob; contentType: string }
  | { outcome: 'not-found' }
  | { outcome: 'unauthenticated' }
  | { outcome: 'network-error' }
  | { outcome: 'server-error'; status: number };

function outcomeForStatus(status: number): 'unauthenticated' | 'unavailable' | 'server-error' {
  if (status === 401) return 'unauthenticated';
  if (status === 503) return 'unavailable';
  return 'server-error';
}

export async function pushTables(
  tables: Partial<Record<SyncTableKey, Record<string, unknown>[]>>,
  cursor: string | null,
  fetchImpl: typeof fetch = fetch,
): Promise<PushOutcome> {
  let response: Response;
  try {
    response = await fetchImpl('/api/sync/push', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      // `cursor` is this device's own last-saved *pull* cursor, forwarded
      // unmodified — it lets the server distinguish a genuinely stale push
      // from an intentional one when a row is currently tombstoned. `null`
      // (never pulled yet) is sent as an absent field, the server's own
      // conservative default.
      body: JSON.stringify(cursor === null ? { tables } : { tables, cursor }),
    });
  } catch {
    return { outcome: 'network-error' };
  }

  if (response.status === 200) {
    const body = (await response.json()) as { results: Partial<Record<SyncTableKey, PushRowResult[]>> };
    return { outcome: 'success', results: body.results };
  }

  const kind = outcomeForStatus(response.status);
  return kind === 'server-error' ? { outcome: 'server-error', status: response.status } : { outcome: kind };
}

export async function pullTables(cursor: string | null, fetchImpl: typeof fetch = fetch): Promise<PullOutcome> {
  const url = cursor === null || cursor.length === 0 ? '/api/sync/pull' : `/api/sync/pull?cursor=${encodeURIComponent(cursor)}`;

  let response: Response;
  try {
    response = await fetchImpl(url, { method: 'GET', credentials: 'include' });
  } catch {
    return { outcome: 'network-error' };
  }

  if (response.status === 200) {
    const body = (await response.json()) as {
      cursor: string;
      hasMore: boolean;
      tables: Partial<Record<SyncTableKey, { rows: Record<string, unknown>[]; hasMore: boolean }>>;
    };
    return { outcome: 'success', cursor: body.cursor, hasMore: body.hasMore, tables: body.tables };
  }

  const kind = outcomeForStatus(response.status);
  return kind === 'server-error' ? { outcome: 'server-error', status: response.status } : { outcome: kind };
}

export async function uploadMedia(
  mediaId: string,
  blob: Blob,
  contentType: string,
  fetchImpl: typeof fetch = fetch,
): Promise<MediaUploadOutcome> {
  let response: Response;
  try {
    response = await fetchImpl(`/api/sync/media/${encodeURIComponent(mediaId)}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': contentType },
      body: blob,
    });
  } catch {
    return { outcome: 'network-error' };
  }

  if (response.status === 200) return { outcome: 'success' };
  if (response.status === 404) return { outcome: 'not-found' };

  const kind = outcomeForStatus(response.status);
  return kind === 'unavailable' || kind === 'server-error'
    ? { outcome: 'server-error', status: response.status }
    : { outcome: kind };
}

export async function downloadMedia(mediaId: string, fetchImpl: typeof fetch = fetch): Promise<MediaDownloadOutcome> {
  let response: Response;
  try {
    response = await fetchImpl(`/api/sync/media/${encodeURIComponent(mediaId)}`, {
      method: 'GET',
      credentials: 'include',
    });
  } catch {
    return { outcome: 'network-error' };
  }

  if (response.status === 200) {
    const blob = await response.blob();
    return { outcome: 'success', blob, contentType: response.headers.get('Content-Type') ?? 'application/octet-stream' };
  }
  if (response.status === 404) return { outcome: 'not-found' };

  const kind = outcomeForStatus(response.status);
  return kind === 'unavailable' || kind === 'server-error'
    ? { outcome: 'server-error', status: response.status }
    : { outcome: kind };
}
